/* eslint-disable max-lines */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import {
  IEsimPackage,
  ILocationZone
} from "../../interfaces/esim/EsimPackages.types";
import {
  getLocationZones,
  getPackageById,
  getPackagesByLocationZone
} from "../../services/Esim.service";
import { SelectOption } from "../ui/input/Input.types";
import SelectInput from "../ui/input/SelectInput";

interface EsimPackageSelectorProps {
  value: number | null | undefined;
  onChange: (
    packageId: number | undefined,
    pkg: IEsimPackage | null
  ) => void;
  error?: string;
}

const formatGB = (gb: number, isUnlimited: boolean) => {
  if (isUnlimited) return "Unlimited";
  return gb >= 1 ? `${gb} GB` : `${Math.round(gb * 1024)} MB`;
};

const EsimPackageSelector: React.FC<EsimPackageSelectorProps> = ({
  value,
  onChange,
  error
}) => {
  const [zones, setZones] = useState<ILocationZone[]>([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [selectedZoneSlug, setSelectedZoneSlug] = useState<string | null>(null);
  const [packages, setPackages] = useState<IEsimPackage[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const fetchZones = async () => {
      setZonesLoading(true);
      try {
        const res = await getLocationZones("", 1, 100);
        if (cancelled) return;
        if (res.data.succeeded) {
          setZones(res.data.data);
        }
      } catch {
        if (!cancelled) toast.error("Gabim gjate ngarkimit te zonave.");
      } finally {
        if (!cancelled) setZonesLoading(false);
      }
    };
    void fetchZones();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!value || initializedRef.current) return;
    initializedRef.current = true;

    const resolve = async () => {
      setInitializing(true);
      try {
        const pkgRes = await getPackageById(value);
        const pkg = pkgRes.data;
        if (!pkg) return;

        setSelectedZoneSlug(pkg.locationZoneSlug);

        const siblingsRes = await getPackagesByLocationZone(
          pkg.locationZoneSlug
        );
        if (siblingsRes.data) {
          setPackages(siblingsRes.data);
        }
      } catch {
        // Silent — user can still pick manually.
      } finally {
        setInitializing(false);
      }
    };
    void resolve();
  }, [value]);

  useEffect(() => {
    if (initializing || !selectedZoneSlug) return;

    let cancelled = false;
    const fetchPackages = async () => {
      setPackagesLoading(true);
      try {
        const res = await getPackagesByLocationZone(selectedZoneSlug);
        if (cancelled) return;
        if (res.data) {
          setPackages(res.data);
        }
      } catch {
        if (!cancelled) toast.error("Gabim gjate ngarkimit te paketave.");
      } finally {
        if (!cancelled) setPackagesLoading(false);
      }
    };
    void fetchPackages();
    return () => {
      cancelled = true;
    };
  }, [selectedZoneSlug, initializing]);

  const zoneOptions = useMemo<SelectOption[]>(
    () =>
      zones.map((z) => ({
        id: z.slug,
        name: z.name,
        imageUrl: z.imageUrl
      })),
    [zones]
  );

  const packageOptions = useMemo<SelectOption[]>(
    () =>
      packages.map((p) => ({
        id: p.id,
        name: `${p.name} — ${formatGB(p.totalGB, p.isUnlimited)}, ${
          p.validityDays
        } ditë, ${p.cost} EUR`,
        imageUrl: p.imageUrl
      })),
    [packages]
  );

  const selectedPackage = useMemo(
    () => packages.find((p) => p.id === value) ?? null,
    [packages, value]
  );

  const handleZoneSelect = (opt: SelectOption | null) => {
    const newSlug = opt ? String(opt.id) : null;
    setSelectedZoneSlug(newSlug);
    setPackages([]);
    if (selectedPackage) {
      onChange(undefined, null);
    }
  };

  const handlePackageSelect = (opt: SelectOption | null) => {
    if (!opt) return;
    const pkg = packages.find((p) => p.id === opt.id);
    if (pkg) onChange(pkg.id, pkg);
  };

  return (
    <div className="mb-6">
      <SelectInput
        label="Zona e lokacionit"
        placeholder="Zgjidh zonën"
        options={zoneOptions}
        selectedId={selectedZoneSlug ?? ""}
        loading={zonesLoading || initializing}
        onOptionSelect={handleZoneSelect}
      />

      {selectedZoneSlug && (
        <div className="mt-4">
          <SelectInput
            label="Paketa eSIM"
            placeholder={
              packagesLoading ? "Duke ngarkuar paketat..." : "Zgjidh paketën"
            }
            options={packageOptions}
            selectedId={value ?? ""}
            loading={packagesLoading}
            error={error}
            onOptionSelect={handlePackageSelect}
          />
        </div>
      )}

      {selectedPackage && (
        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-xs text-green-700">
            Paketa e zgjedhur: <strong>{selectedPackage.name}</strong> —{" "}
            {formatGB(selectedPackage.totalGB, selectedPackage.isUnlimited)},{" "}
            {selectedPackage.validityDays} ditë, {selectedPackage.cost} EUR
          </p>
        </div>
      )}
    </div>
  );
};

export default EsimPackageSelector;
